import React, { FC, useMemo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../../hooks/locale'
import Distances from './distances'
import NearBySection from './near-by'
import { Typography } from 'react-components'
import { ComponentSeparator, ExposeDesktopWrapper, ExposeSectionTitleWrapper, StyledGhostButton } from '../style'
import { SurroundingLocation, Location as LocationTransport } from '../../../transports/property-details-page-transport'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { SECTION_IDS } from '../constants'
import config from '../../../common/config'
import { EmbeddedMapFrame, MapPlaceholderWrapper } from './styles'
import LocationBar from '../location-bar'
import { FormattedBreadcrumbTransport } from '../../../models/property-details-page/breadcrumb-model'
import AnalyticsService from '../../../services/analytics-service'
import { getBasePath } from '../../../utils/miscellaneous-utils'
import { CookieManagerService } from '../../../services'

type LocationProps = {
    location: LocationTransport
    surroundings: Array<SurroundingLocation>
    activities: string[]
    leisure: string
    journey: string
    surroundingLocation: string
    regionLabel: string
    region: FormattedBreadcrumbTransport | null
    hideExactPosition: boolean
}

const Location: FC<LocationProps> = ({
    location,
    surroundings,
    activities,
    leisure,
    surroundingLocation,
    journey,
    regionLabel,
    region,
    hideExactPosition,
}) => {
    const { t } = useTranslation()

    const latitude = get(location, 'latitude')
    const longitude = get(location, 'longitude')

    // Generate Google Embedded Maps URL
    const embeddedMapUrl = useMemo(() => {
        if (latitude === null || longitude === null) {
            return null
        }

        const baseUrl = 'https://www.google.com/maps/embed/v1/place'
        const url = new URL(baseUrl)
        url.searchParams.set('key', config.googleEmbeddedMapsKey)
        url.searchParams.set('q', `${latitude},${longitude}`)
        url.searchParams.set('zoom', '15')
        url.searchParams.set('maptype', 'roadmap')

        return url.toString()
    }, [latitude, longitude])

    const hasLocation = !!embeddedMapUrl
    const hasSurroundings = !isEmpty(surroundings)
    const hasActivities = !isEmpty(activities)
    const hasJourneyInfo = !isEmpty(journey)
    
    // Render section if we have map OR any other location content
    const hasAnyLocationContent = hasLocation || hasSurroundings || hasActivities || hasJourneyInfo

    const [hasFunctionalConsent, setHasFunctionalConsent] = useState(false)

    const checkConsentIfGiven = useCallback(() => {
        if (CookieManagerService.hasConsentFor('functional')) {
            setHasFunctionalConsent(true)
            return true
        }
        return false
    }, [])

    useEffect(() => {
        checkConsentIfGiven()

        const listener = () => checkConsentIfGiven()

        document.addEventListener('onCloseCCforMap', listener)
        return () => document.removeEventListener('onCloseCCforMap', listener)
    }, [checkConsentIfGiven])

    const handleMapLoadClick = useCallback(() => {
        AnalyticsService.trackEvent({
            action: 'click_load_map_button',
            category: 'Expose',
            label: 'tbd',
        })
    }, [])

    if (!hasAnyLocationContent) {
      return null
    }

    return (
        <section id={SECTION_IDS.DISTANCES_SECTION_ID}>
            <ExposeSectionTitleWrapper>
                <Typography variant="h6" fontWeight="semiBold" element="h2">
                    {t('locationTitle')}
                </Typography>
            </ExposeSectionTitleWrapper>

            {/* Render the Google Embedded Map if coordinates are valid */}
            {hasLocation &&
                !hideExactPosition &&
                (hasFunctionalConsent ? (
                    <EmbeddedMapFrame
                        src={embeddedMapUrl}
                        title="Property location map"
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        backgroundUrl={getBasePath('/assets/images/hero/map-preview.jpg')}
                    />
                ) : (
                    <MapPlaceholderWrapper
                        backgroundUrl={getBasePath('/assets/images/hero/map-preview.jpg')}
                        aria-label="Map preview placeholder"
                    >
                        <StyledGhostButton
                            isContentWidth
                            id="cc-map-popup-button"
                            onClick={handleMapLoadClick}
                            color="primary"
                        >
                            {t('showTheLocationOfTheProperty')}
                        </StyledGhostButton>
                    </MapPlaceholderWrapper>
                ))}

            <ExposeDesktopWrapper>
                <LocationBar showMapHyperlink={false} region={region} />
            </ExposeDesktopWrapper>

            {/* Render distances if surroundings data is available */}
            {hasSurroundings && <Distances distances={surroundings} />}

            {/* Render nearby section if activities or journey info is available */}
            {(hasActivities || hasJourneyInfo) && (
                <NearBySection
                    activities={activities}
                    leisure={leisure}
                    surroundings={surroundingLocation}
                    journey={journey}
                    regionLabel={regionLabel}
                />
            )}

            <ComponentSeparator />
        </section>
    )
}

export default Location
